import { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Switch } from '@/components/ui/switch'
import { ArrowLeft } from 'lucide-react'
import { deviceTypeService } from '@/services/deviceType.service'
import { useToast } from '@/components/ui/use-toast'

const deviceTypeSchema = z.object({
  description: z.string().optional(),
  expected_interval_minutes: z.number().optional(),
  missed_transmissions: z.number().optional(),
  is_active: z.boolean().default(true)
})

type DeviceTypeFormData = z.infer<typeof deviceTypeSchema>

export const DeviceTypeEditPage = () => {
  const { deviceTypeId } = useParams<{ deviceTypeId: string }>()
  const navigate = useNavigate()
  const { toast } = useToast()
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingData, setIsLoadingData] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [deviceTypeName, setDeviceTypeName] = useState('')
  const isEditMode = deviceTypeId && deviceTypeId !== 'new'

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors }
  } = useForm<DeviceTypeFormData>({
    resolver: zodResolver(deviceTypeSchema),
    defaultValues: {
      description: '',
      expected_interval_minutes: undefined,
      missed_transmissions: undefined,
      is_active: true
    }
  })

  const isActive = watch('is_active')
  const expectedIntervalMinutes = watch('expected_interval_minutes')
  const missedTransmissions = watch('missed_transmissions')

  // Load device type data in edit mode
  useEffect(() => {
    const loadDeviceType = async () => {
      if (!isEditMode || !deviceTypeId) {
        setIsLoadingData(false)
        return
      }

      try {
        const data = await deviceTypeService.get(deviceTypeId)
        setDeviceTypeName(data.device_type)
        reset({
          description: data.description || '',
          expected_interval_minutes: data.expected_interval_minutes,
          missed_transmissions: data.missed_transmissions,
          is_active: data.is_active
        })
      } catch (err: any) {
        console.error('Failed to load device type:', err)
        toast({
          title: 'Error',
          description: err.response?.data?.detail || 'Failed to load device type',
          variant: 'destructive'
        })
        navigate('/admin/device-types')
      } finally {
        setIsLoadingData(false)
      }
    }

    loadDeviceType()
  }, [deviceTypeId, isEditMode, navigate, reset, toast])

  const onSubmit = async (data: DeviceTypeFormData) => {
    if (!isEditMode || !deviceTypeId) {
      toast({
        title: 'Error',
        description: 'Device type creation is not supported. Device types can only be edited.',
        variant: 'destructive'
      })
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      await deviceTypeService.update(deviceTypeId, {
        description: data.description,
        expected_interval_minutes: data.expected_interval_minutes,
        missed_transmissions: data.missed_transmissions,
        is_active: data.is_active
      })

      toast({
        title: 'Success',
        description: 'Device type updated successfully'
      })

      navigate('/admin/device-types')
    } catch (err: any) {
      const errorMsg = err.response?.data?.detail || err.response?.data?.message || 'Failed to update device type'
      setError(errorMsg)
      toast({
        title: 'Error',
        description: errorMsg,
        variant: 'destructive'
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" asChild>
          <Link to="/admin/device-types">
            <ArrowLeft className="h-4 w-4" />
          </Link>
        </Button>
        <div>
          <h1 className="text-3xl font-bold">
            {isEditMode ? `Edit Device Type: ${deviceTypeName}` : 'Device Type Configuration'}
          </h1>
          <p className="text-muted-foreground">
            {isEditMode ? 'Update device type configuration settings' : 'Device types can only be edited, not created'}
          </p>
        </div>
      </div>

      {isLoadingData ? (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            Loading device type...
          </CardContent>
        </Card>
      ) : !isEditMode ? (
        <Card>
          <CardContent className="py-8 text-center">
            <p className="text-muted-foreground mb-4">
              Device types are defined in the system and can only be edited.
            </p>
            <Button asChild>
              <Link to="/admin/device-types">View Device Types</Link>
            </Button>
          </CardContent>
        </Card>
      ) : (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Device Type Configuration</CardTitle>
              <CardDescription>Update the settings for this device type</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {error && (
                <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
                  {error}
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  {...register('description')}
                  placeholder="Description of this device type"
                  rows={3}
                />
                {errors.description && (
                  <p className="text-sm text-destructive">{errors.description.message}</p>
                )}
              </div>

              <div className="grid gap-6 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="expected_interval_minutes">Expected Interval (minutes)</Label>
                  <Input
                    id="expected_interval_minutes"
                    type="number"
                    {...register('expected_interval_minutes', { valueAsNumber: true })}
                    placeholder="60"
                  />
                  {errors.expected_interval_minutes && (
                    <p className="text-sm text-destructive">{errors.expected_interval_minutes.message}</p>
                  )}
                  <p className="text-xs text-muted-foreground">
                    Expected time between device transmissions
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="missed_transmissions">Missed Transmissions Threshold</Label>
                  <Input
                    id="missed_transmissions"
                    type="number"
                    {...register('missed_transmissions', { valueAsNumber: true })}
                    placeholder="3"
                  />
                  {errors.missed_transmissions && (
                    <p className="text-sm text-destructive">{errors.missed_transmissions.message}</p>
                  )}
                  <p className="text-xs text-muted-foreground">
                    Number of missed transmissions before alerting
                  </p>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  id="is_active"
                  checked={isActive}
                  onCheckedChange={(checked) => setValue('is_active', checked)}
                />
                <Label htmlFor="is_active" className="text-sm font-normal cursor-pointer">
                  Active (devices of this type can be created and used)
                </Label>
              </div>
            </CardContent>
          </Card>

          <div className="flex gap-4">
            <Button type="submit" disabled={isLoading}>
              {isLoading ? 'Updating...' : 'Update Device Type'}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/admin/device-types')}
            >
              Cancel
            </Button>
          </div>
        </form>
      )}
    </div>
  )
}
